import { format, formatDistanceToNow, parseISO, startOfWeek, endOfWeek, isValid } from 'date-fns';
import { clsx, type ClassValue } from 'clsx';

// Class name utility
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

// Time formatting utilities
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (secs > 0 && minutes < 10) {
    return `${minutes}m ${secs}s`;
  }

  return `${minutes}m`;
}

export function formatTimerDisplay(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const pad = (n: number) => n.toString().padStart(2, '0');

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  }

  return `${pad(minutes)}:${pad(secs)}`;
}

export function formatDate(dateString: string, formatStr = 'MMM d, yyyy'): string {
  const date = parseISO(dateString);
  if (!isValid(date)) return 'Invalid date';
  return format(date, formatStr);
}

export function formatDateTime(dateString: string): string {
  const date = parseISO(dateString);
  if (!isValid(date)) return 'Invalid date';
  return format(date, 'MMM d, yyyy h:mm a');
}

export function formatRelativeTime(dateString: string): string {
  const date = parseISO(dateString);
  if (!isValid(date)) return 'Invalid date';
  return formatDistanceToNow(date, { addSuffix: true });
}

export function getWeekRange(date: Date = new Date()): { start: Date; end: Date } {
  return {
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 }),
  };
}

// Percentage calculation
export function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
}

// Text utilities
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/--+/g, '-')
    .trim();
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatTaskType(type: string): string {
  return type
    .split('_')
    .map((word) => capitalize(word))
    .join(' ');
}

export function formatTaskStatus(status: string): string {
  return status
    .split('_')
    .map((word) => capitalize(word))
    .join(' ');
}

// Color utilities
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    not_started: 'bg-text-tertiary',
    in_progress: 'bg-primary-500',
    paused: 'bg-warning-500',
    completed: 'bg-success-500',
    overdue: 'bg-danger-500',
  };
  return colors[status] || 'bg-text-tertiary';
}

export function getPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    low: 'text-text-tertiary bg-surface-tertiary',
    medium: 'text-warning-700 bg-warning-50',
    high: 'text-danger-700 bg-danger-50',
  };
  return colors[priority] || 'text-text-tertiary bg-surface-tertiary';
}

export function getTaskTypeColor(type: string): string {
  const colors: Record<string, string> = {
    learning: 'text-primary-700 bg-primary-50',
    office: 'text-success-700 bg-success-50',
    research: 'text-info-700 bg-info-50',
    documentation: 'text-warning-700 bg-warning-50',
    interview_prep: 'text-danger-700 bg-danger-50',
    open: 'text-text-secondary bg-surface-tertiary',
  };
  return colors[type] || 'text-text-secondary bg-surface-tertiary';
}

// Reading time estimation
export function estimateReadingTime(content: string): number {
  const wordsPerMinute = 200;
  const wordCount = content.trim().split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
}

// Local storage utilities
export function getFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

export function setToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
}

// Debounce utility
export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => func(...args), wait);
  };
}

// Generate unique ID
export function generateId(): string {
  return crypto.randomUUID();
}

// Array utilities
export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce(
    (groups, item) => {
      const groupKey = String(item[key]);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
      return groups;
    },
    {} as Record<string, T[]>
  );
}

export function sortBy<T>(array: T[], key: keyof T, order: 'asc' | 'desc' = 'asc'): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
}
